import { AfterViewInit, ChangeDetectorRef, Component, EventEmitter, forwardRef, Input, OnChanges, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import * as moment from 'moment';
import { IMyDate, IMyDateModel, IMyOptions, MyDatePicker } from 'mydatepicker';
import { ControlValueAccessor, NG_VALIDATORS, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MdlSelectComponent } from '@angular-mdl/select';
import { merge, Subscription } from 'rxjs';
import { filter, map } from 'rxjs/operators';

enum CalToggle {
    Open = 1,
    CloseByDateSel = 2,
    CloseByCalBtn = 3,
    CloseByOutClick= 4,
    CloseByEsc = 5,
    CloseByApi = 6
}
@Component({
    selector: 'covance-date-picker',
    template: require('./covance-date-picker.component.html'),
    styles: [require('./covance-date-picker.component.scss')],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => CovanceDatePickerComponent),
            multi: true
        },
        {
            provide: NG_VALIDATORS,
            useExisting: CovanceDatePickerComponent,
            multi: true
        }
    ]
    //TODO: fix work with ChangeDetectionStrategy.OnPush
})
export class CovanceDatePickerComponent implements OnInit, AfterViewInit, OnDestroy, OnChanges, ControlValueAccessor {
    @Input()
    public date: moment.MomentInput;

    @Input()
    public disabled: boolean;

    @Input()
    public disableUntil: moment.Moment;

    @Input()
    public allowTimePicker: boolean;

    @ViewChild('datePicker')
    public datePicker: MyDatePicker;

    @ViewChild('selectHours')
    public selectHours: MdlSelectComponent;

    @ViewChild('selectMinutes')
    public selectMinutes: MdlSelectComponent;

    @Output()
    public dateChanged: EventEmitter<moment.Moment> = new EventEmitter<moment.Moment>();

    @Output()
    public onCalendarDateTimeSelect: EventEmitter<boolean> = new EventEmitter<boolean>();

    public isShownDateSelector: boolean;

    public model: { date: IMyDate };

    public datePickerOptions: IMyOptions;

    public get value(): moment.MomentInput {
        return this.date;
    }

    public set value(v: moment.MomentInput) {
        this.date = v;
        this.ngOnChanges();
        this.onChange(this.date);
        this.onTouched();
    }
    public isDisabledTime = false;
    public selectedHours = '00';
    public selectedMinutes = '00';
    public hours: string[];
    public minutes: string[];

    private isValid: boolean;
    private subscription: Subscription;

    public constructor(private ref: ChangeDetectorRef) {
        this.hours = this.getTimeArray(24);
        this.minutes = this.getTimeArray(60);
    }

    public onChange: any = () => { };
    public onTouched: any = () => { };

    public writeValue(date: moment.MomentInput): void {
        if (date) {
            this.value = date;
        } else {
            this.value = moment(null);
        }
    }
    public registerOnChange(fn: any): void {
        this.onChange = fn;
    }
    public registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }
    public setDisabledState?(isDisabled: boolean): void {
        this.datePicker.setDisabledState(isDisabled);
    }

    public ngOnInit() {
        this.datePickerOptions = {
            inline: false,
            width: '130px',
            height: '24px',
            showSelectorArrow: false,
            selectionTxtFontSize: 'inherit',
            indicateInvalidDate: true
        };
        if (this.disableUntil) {
            this.datePickerOptions.disableUntil = convertDate(this.disableUntil);
        }
    }

    public ngAfterViewInit(): void {
        if (this.allowTimePicker && this.selectHours && this.selectMinutes) {
            this.subscription = merge(
                merge(this.selectHours.popoverComponent.onShow, this.selectMinutes.popoverComponent.onShow).pipe(
                    map(() => true)),
                merge(this.selectHours.popoverComponent.onHide, this.selectMinutes.popoverComponent.onHide).pipe(
                    filter(() => !this.isShownDateSelector),
                    map(() => false))
            ).subscribe((v) => {
                this.onCalendarDateTimeSelect.emit(v);
            });
        }
    }

    public ngOnChanges() {
        this.datePicker.locale = moment.locale();
        this.datePicker.setLocaleOptions();
        this.datePicker.placeholder = this.datePicker.opts.dateFormat;
        if (!this.date) {
            this.datePicker.updateDateValue({year: 0, month: 0, day: 0});
            this.isDisabledTime = true;
            this.isValid = true;
            return;
        }

        const date = moment(this.date);
        this.selectedHours = date.format('HH');
        this.selectedMinutes = date.format('mm');
        if (date.isValid()) {
            this.isDisabledTime = false;
            this.model = {
                date: convertDate(date)
            };
            this.isValid = true;
        } else {
            this.isValid = false;
        }
        this.ref.markForCheck();
    }
    public onCalendarToggle(event: number) {
        this.isShownDateSelector = event === CalToggle.Open;
        this.onCalendarDateTimeSelect.emit(this.isShownDateSelector);
        this.closeTimeSelection();
    }

    public onDateChange($event: IMyDateModel) {
        let date = moment($event.jsdate);
        date = date.isValid() ? this.allowTimePicker ? this.getDateTime(date) : date : null;
        this.date = date;
        this.isValid = true;
        this.dateChanged.emit(date);
        this.onChange(this.date);
        this.onTouched();
    }

    public onDateTextChange($event: any) {
        this.isValid = $event.value ? $event.valid : true;
        this.onChange(this.date);
        this.onTouched();
    }

    public validate() {
        return this.isValid || {
            invalid: true
          };
    }

    public getErrorText() {
        return this.isValid ? 'This field is required' : 'invalid date string';
    }

    public isInvalidDate() {
        return this.datePicker.invalidDate;
    }

    public onChangeTime() {
        const date = moment(this.date, 'YYYY-MM-DD');
        const dateTime = this.getDateTime(date);
        this.isValid = true;
        this.dateChanged.emit(dateTime);
        this.onChange(this.date);
        this.onTouched();
        this.closeTimeSelection();
    }

    public ngOnDestroy(): void {
        if (this.subscription) {
            this.subscription.unsubscribe();
        }
    }

    private getDateTime(date: moment.Moment) {
        return date
            ? date.add(this.selectedMinutes, 'minutes').add(this.selectedHours, 'hours')
            : date;
    }

    private getTimeArray(numberOfElements: number) {
        return new Array(numberOfElements)
            .fill(0)
            .map((v, i) => {
                return ('0' + i).substr(-2, 2);
            });
    }

    private closeTimeSelection() {
        if (this.selectHours && this.selectMinutes) {
            this.selectHours.popoverComponent.hide();
            this.selectMinutes.popoverComponent.hide();
        }
    }
}

const convertDate = (date: moment.Moment): IMyDate => ({
    year: date.year(),
    month: date.month() + 1,
    day: date.date()
});
